'use client';

import { MarketType } from '@velocity-exchange/sdk';
import { MarketId, UIMarket, getMarketKey } from '@velocity-exchange/common';
import { useCallback, useMemo } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

const useFavouriteMarkets = () => {
	const btcMarketKey = getMarketKey(1, MarketType.PERP);
	const solMarketKey = getMarketKey(0, MarketType.PERP);
	const [favoriteMarkets, setFavoriteMarkets] = useSyncLocalStorage<string[]>(
		'favoriteMarkets',
		[btcMarketKey, solMarketKey]
	);
	const { captureEvent } = usePostHogCapture();

	const addFavourite = useCallback(
		(newMarket: UIMarket) => {
			setFavoriteMarkets([
				...favoriteMarkets,
				getMarketKey(newMarket.market.marketIndex, newMarket.marketType),
			]);
		},
		[favoriteMarkets]
	);

	const removeFavourite = useCallback(
		(marketToRemove: UIMarket) => {
			setFavoriteMarkets(
				favoriteMarkets.filter(
					(marketKey) =>
						marketKey !==
						getMarketKey(
							marketToRemove.market.marketIndex,
							marketToRemove.marketType
						)
				)
			);
		},
		[favoriteMarkets]
	);

	const toggleFavourite = useCallback(
		(market: UIMarket) => {
			const isFavorited = favoriteMarkets.includes(
				getMarketKey(market.market.marketIndex, market.marketType)
			);

			if (isFavorited) {
				removeFavourite(market);
			} else {
				addFavourite(market);
			}

			// If the market is currently favorited, that means we're unfavoriting, and vice versa.
			const event = isFavorited
				? 'trade_favorite_market_removed'
				: 'trade_favorite_market_added';

			captureEvent(event, { market_symbol: market.market.symbol });
		},
		[favoriteMarkets, addFavourite, removeFavourite, captureEvent]
	);

	const getFavouriteMarketIndex = useCallback(
		(market: MarketId) => {
			return favoriteMarkets.findIndex((mkt) => mkt === market.key);
		},
		[favoriteMarkets]
	);

	const favouriteMarketsIncludes = useCallback(
		(market: MarketId) => {
			return getFavouriteMarketIndex(market) !== -1;
		},
		[favoriteMarkets]
	);

	const result = useMemo(() => {
		return {
			favoriteMarkets,
			addFavourite,
			removeFavourite,
			toggleFavourite,
			favouriteMarketsIncludes,
			getFavouriteMarketIndex,
		};
	}, [favoriteMarkets, addFavourite, removeFavourite, toggleFavourite]);

	return result;
};

export default singletonHook(
	{
		favoriteMarkets: [],
		addFavourite: (_newMarket: UIMarket) => {},
		removeFavourite: (_marketToRemove: UIMarket) => {},
		toggleFavourite: (_market: UIMarket) => {},
		favouriteMarketsIncludes: (_market: MarketId) => false,
		getFavouriteMarketIndex: (_market: MarketId) => -1,
	},
	useFavouriteMarkets
);
